/**
 * F 154 C1 — the raw-markdown editor shell.
 *
 * A textarea plus a loss notice. Deliberately dumb: every decision it makes is
 * delegated to `markdown-toggle.ts`, which is pure and unit-tested. This file
 * only owns the interaction.
 *
 * WHY NOT REUSE `RawMarkdownView`: that primitive is display-only (it renders
 * a read-only markdown preview with a drop notice). The toggle needs an
 * EDITABLE surface, and a "read-only view with a warning" bolted onto a
 * textarea would be a different component wearing the same name.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  openRawEditor,
  closeRawEditor,
  summariseDrops,
  type LossyWarning,
  type ToggleOutcome,
} from '../../core/editor/markdown-toggle';

export interface RawMarkdownEditorProps {
  /** The canonical blocks, serialised on mount. */
  blocks: unknown;
  /** Called with the newly-parsed blocks when the user commits. */
  onCommit: (blocks: unknown[]) => void;
  /** Called when the user abandons the raw view. */
  onCancel: () => void;
  className?: string;
}

type Phase =
  | { kind: 'editing'; draft: string }
  | { kind: 'blocked'; warning: LossyWarning; draft: string };

export const RawMarkdownEditor: React.FC<RawMarkdownEditorProps> = ({
  blocks,
  onCommit,
  onCancel,
  className = '',
}) => {
  // Project ONCE on mount. Re-projecting on every render would fight the
  // user's cursor mid-edit — the whole point of the raw view is that what you
  // see is what you typed.
  const [warning] = useState<LossyWarning>(() => openRawEditor(blocks));
  const [phase, setPhase] = useState<Phase>({ kind: 'editing', draft: warning.preview });

  const attempt = useCallback(
    (force: boolean): ToggleOutcome => {
      const draft = phase.kind === 'blocked' ? phase.draft : (phase as { draft: string }).draft;
      const outcome = closeRawEditor(draft, { force });
      if (outcome.ok) {
        onCommit(outcome.blocks as unknown[]);
        return outcome;
      }
      if (outcome.reason === 'lossy') {
        setPhase({ kind: 'blocked', warning: outcome.warning, draft });
      }
      return outcome;
    },
    [phase, onCommit],
  );

  // Warn on mount, before the user has typed anything. "You are about to
  // degrade this document" is information the user needs at OPEN time, not
  // after they have edited for five minutes.
  useEffect(() => {
    if (!warning.isLossless && phase.kind === 'editing') {
      setPhase((p) => (p.kind === 'editing' ? { kind: 'blocked', warning, draft: p.draft } : p));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warning.isLossless]);

  const draft = phase.kind === 'blocked' ? phase.draft : (phase as { draft: string }).draft;

  return (
    <div className={`fc-raw-editor ${className}`.trim()} data-testid="fc-raw-editor">
      {phase.kind === 'blocked' && (
        <div className="fc-raw-editor__warning" role="alert" data-testid="fc-raw-lossy-warning">
          <strong>{summariseDrops(phase.warning.drops)}</strong>
          <ul>
            {phase.warning.drops.map((d) => (
              <li key={`${d.reason}:${d.blockType}`}>
                <code>{d.blockType}</code> — {d.message}
                {d.count > 1 ? ` (×${d.count})` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      <textarea
        className="fc-raw-editor__textarea"
        data-testid="fc-raw-textarea"
        value={draft}
        spellCheck={false}
        onChange={(e) => {
          const v = e.currentTarget.value;
          setPhase((p) => ({ kind: 'editing', draft: v }));
        }}
      />

      <div className="fc-raw-editor__actions">
        <button type="button" data-testid="fc-raw-cancel" onClick={onCancel}>
          Cancel
        </button>
        {phase.kind === 'blocked' ? (
          <button
            type="button"
            data-testid="fc-raw-force"
            onClick={() => attempt(true)}
          >
            Discard lossy parts and apply
          </button>
        ) : (
          <button type="button" data-testid="fc-raw-apply" onClick={() => attempt(false)}>
            Apply
          </button>
        )}
      </div>
    </div>
  );
};

export default RawMarkdownEditor;
